import React, { useState } from "react";
import {
  TextField,
  Button,
  Container,
  Typography,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  IconButton,
  Link,
  // Divider,
  Paper,
  Grid,
  FormHelperText,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  PersonOutlined,
  EmailOutlined,
  LockOutlined,
  PhoneOutlined,
  HomeOutlined
} from "@mui/icons-material";
// import GoogleIcon from "@mui/icons-material/Google";
// import FacebookIcon from "@mui/icons-material/Facebook";
import { useForm, Controller } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { backendURL } from "../url";
import {
  HEAR_ABOUT_US_SOURCES,
  HEAR_ABOUT_US_OTHER_MAX_LENGTH,
  HearAboutUsSource,
} from "../constants/hearAboutUs";

interface SignupFormInputs {
  firstName: string;
  lastName: string;
  userType: string;
  email: string;
  phoneNumber: string;
  address: string;
  password: string;
  confirmPassword?: string;
  heardAboutUs: HearAboutUsSource | "";
  heardAboutUsOther?: string;
  promotionalConsent: boolean;
}

const Signup: React.FC = () => {
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<SignupFormInputs>();

  const heardAboutUs = watch("heardAboutUs");
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] =
    useState<boolean>(false);
  const navigate = useNavigate();

  const onSubmit = async (data: SignupFormInputs) => {
    // Remove confirmPassword before sending to backend
    const { confirmPassword, heardAboutUsOther, promotionalConsent, ...submitData } = data;
    const pendingReferralCode = localStorage.getItem("pendingReferralCode");

    try {
      const response = await axios.post(
        // `${process.env.REACT_APP_BACKEND_URL}/api/auth/signup`,
        `${backendURL}/api/auth/signup`,
        {
          ...submitData,
          heardAboutUsOther:
            data.heardAboutUs === "others"
              ? heardAboutUsOther?.trim()
              : undefined,
          promotionalEmails: promotionalConsent === true,
          promotionalSms: promotionalConsent === true,
          ...(pendingReferralCode ? { referralCode: pendingReferralCode } : {}),
        }
      );
      localStorage.setItem("token", response.data.token);
      navigate("/login");
    } catch (error: any) {
      if (error.response?.status === 400) {
        setError(error.response?.data?.message || "User already exists");
      } else {
        setError("Signup failed. Please try again.");
      }
      console.error("Signup error:", error);
    }
  };

  return (
    <Box
      component="main"
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        // padding: { xs: "64px 0 32px", md: "80px 0 40px" },
        background: "linear-gradient(135deg, rgb(183, 194, 240) 0%, #764ba2 100%)",
      }}
    >
      <Container maxWidth="md">
        <Paper
          elevation={10}
          sx={{
            p: { xs: 3, md: 4 },
            borderRadius: 4,
            boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.37)",
            bgcolor: "white",
            transition: "transform 0.3s ease",
            "&:hover": {
              transform: "translateY(-5px)",
            },
          }}
        >
          <Typography
            variant="h4"
            textAlign="center"
            mb={3}
            sx={{
              fontWeight: "bold",
              color: "#333",
              fontFamily: "'Poppins', sans-serif",
            }}
          >
            Create Account
          </Typography>

          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={2}>
              {/* First row - Name fields */}
              <Grid item xs={12} sm={6}>
                <Controller
                  name="firstName"
                  control={control}
                  defaultValue=""
                  rules={{
                    required: "First name is required",
                    pattern: {
                      value: /^[A-Za-z]+$/,
                      message: "First name must contain only letters",
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="First Name"
                      fullWidth
                      error={!!errors.firstName}
                      helperText={errors.firstName?.message}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonOutlined />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="lastName"
                  control={control}
                  defaultValue=""
                  rules={{
                    required: "Last name is required",
                    pattern: {
                      value: /^[A-Za-z]+$/,
                      message: "Last name must contain only letters",
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Last Name"
                      fullWidth
                      error={!!errors.lastName}
                      helperText={errors.lastName?.message}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonOutlined />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />
                  )}
                />
              </Grid>

              {/* Second row - User Type and Email */}
              <Grid item xs={12} sm={6}>
                <Controller
                  name="userType"
                  control={control}
                  defaultValue="user"
                  rules={{ required: "User type is required" }}
                  render={({ field }) => (
                    <FormControl fullWidth required>
                      <InputLabel id="user-type-label">Select User Type</InputLabel>
                      <Select
                        {...field}
                        labelId="user-type-label"
                        label="Select User Type"
                        sx={{ borderRadius: 2 }}
                      >
                        <MenuItem value="user">User</MenuItem>
                        {/* <MenuItem value="customer">Customer</MenuItem> */}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="email"
                  control={control}
                  defaultValue=""
                  rules={{
                    required: "Email is required",
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: "Invalid email address",
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Email"
                      fullWidth
                      type="email"
                      error={!!errors.email}
                      helperText={errors.email?.message}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailOutlined />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />
                  )}
                />
              </Grid>

              {/* Third row - Phone and Address */}
              <Grid item xs={12} sm={6}>
                <Controller
                  name="phoneNumber"
                  control={control}
                  defaultValue=""
                  rules={{
                    required: "Phone number is required",
                    maxLength: {
                      value: 10,
                      message: "Phone number must be exactly 10 digits",
                    }
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Phone Number"
                      fullWidth
                      type="tel"
                      error={!!errors.phoneNumber}
                      helperText={errors.phoneNumber?.message}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PhoneOutlined />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="address"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Address"
                      fullWidth
                      type="text"
                      error={!!errors.address}
                      helperText={errors.address?.message}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <HomeOutlined />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="heardAboutUs"
                  control={control}
                  defaultValue=""
                  rules={{
                    required: "Please tell us how you heard about us",
                  }}
                  render={({ field }) => (
                    <FormControl fullWidth required error={!!errors.heardAboutUs}>
                      <InputLabel id="hear-about-us-label">
                        How did you hear about us?
                      </InputLabel>
                      <Select
                        {...field}
                        labelId="hear-about-us-label"
                        label="How did you hear about us?"
                        sx={{ borderRadius: 2 }}
                      >
                        {HEAR_ABOUT_US_SOURCES.map((source) => (
                          <MenuItem key={source.value} value={source.value}>
                            {source.label}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        {errors.heardAboutUs?.message}
                      </FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>

              {heardAboutUs === "others" && (
                <Grid item xs={12}>
                  <Controller
                    name="heardAboutUsOther"
                    control={control}
                    defaultValue=""
                    rules={{
                      required: "Please tell us where you heard about us",
                      validate: (value) =>
                        (value && value.trim().length > 0) ||
                        "Please tell us where you heard about us",
                      maxLength: {
                        value: HEAR_ABOUT_US_OTHER_MAX_LENGTH,
                        message: `Please keep this under ${HEAR_ABOUT_US_OTHER_MAX_LENGTH} characters`,
                      },
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Please specify"
                        fullWidth
                        required
                        error={!!errors.heardAboutUsOther}
                        helperText={errors.heardAboutUsOther?.message}
                        inputProps={{
                          maxLength: HEAR_ABOUT_US_OTHER_MAX_LENGTH,
                        }}
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                      />
                    )}
                  />
                </Grid>
              )}

              {/* Fourth row - Password fields */}
              <Grid item xs={12} sm={6}>
                <Controller
                  name="password"
                  control={control}
                  defaultValue=""
                  rules={{
                    required: "Password is required",
                    minLength: {
                      value: 8,
                      message: "Password must be at least 8 characters",
                    },
                    pattern: {
                      value:
                        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@#$!%*?&])[A-Za-z\d@#$!%*?&]{8,}$/,
                      message:
                        "Password must include uppercase, lowercase, number, and special character",
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      type={showPassword ? "text" : "password"}
                      label="Password"
                      fullWidth
                      error={!!errors.password}
                      helperText={errors.password?.message}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockOutlined />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="confirmPassword"
                  control={control}
                  defaultValue=""
                  rules={{
                    required: "Please confirm your password",
                    validate: (value) =>
                      value === watch("password") || "Passwords do not match",
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      type={showConfirmPassword ? "text" : "password"}
                      label="Confirm Password"
                      fullWidth
                      error={!!errors.confirmPassword}
                      helperText={errors.confirmPassword?.message}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockOutlined />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() =>
                                setShowConfirmPassword(!showConfirmPassword)
                              }
                              edge="end"
                            >
                              {showConfirmPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="promotionalConsent"
                  control={control}
                  defaultValue={false}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={field.value}
                          onChange={(event) => field.onChange(event.target.checked)}
                          color="primary"
                        />
                      }
                      label="I consent to receive informational, promotional email and SMS from The Bat Cage"
                    />
                  )}
                />
              </Grid>
            </Grid>

            {error && (
              <Typography color="error" textAlign="center" sx={{ mt: 2 }}>
                {error}
              </Typography>
            )}

            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              sx={{
                mt: 3,
                py: 1.5,
                fontWeight: "bold",
                borderRadius: 2,
                boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 6px 8px rgba(0,0,0,0.15)",
                },
              }}
            >
              Sign Up
            </Button>

            {/* <Divider sx={{ my: 3 }}>
              <Typography color="textSecondary" sx={{ fontSize: "0.9rem" }}>Or sign up with</Typography>
            </Divider>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<GoogleIcon />}
                  onClick={handleGoogleSignup}
                  sx={{ 
                    py: 1,
                    borderRadius: 2,
                    border: '1px solid #DADCE0',
                    color: '#5F6368',
                    '&:hover': {
                      backgroundColor: 'rgba(66, 133, 244, 0.04)',
                      borderColor: '#4285F4'
                    }
                  }}
                >
                  Google
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<FacebookIcon />}
                  onClick={handleFacebookSignup}
                  sx={{ 
                    py: 1,
                    borderRadius: 2,
                    border: '1px solid #E7EAF0',
                    color: '#4267B2',
                    '&:hover': {
                      backgroundColor: 'rgba(66, 103, 178, 0.04)',
                      borderColor: '#4267B2'
                    }
                  }}
                >
                  Facebook
                </Button>
              </Grid>
            </Grid> */}

            <Typography variant="body2" textAlign="center" mt={3} mb={1}>
              Already have an account?{" "}
              <Link 
                href="/login" 
                color="primary" 
                underline="hover"
                sx={{ fontWeight: 'medium' }}
              >
                Login
              </Link>
            </Typography>
          </form>
        </Paper>
      </Container>
    </Box>
  );
};

export default Signup;